import Icon from "@components/library/Icon";
import { COLORS, FONTS, SPACINGS, STYLES } from "@constants";
import { IconName } from "@tsTypes/icons";
import { Size } from "@tsTypes/ui";
import { forwardRef, ReactNode } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

export interface BaseButtonProps {
  id?: string;
  name?: string;
  disabled?: boolean;
  type?: "submit" | "reset";
  form?: string;
  onClick?: (...args: any[]) => any;
  onMouseEnter?: (...args: any[]) => any;
  onMouseLeave?: (...args: any[]) => any;
  onFocus?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  margin?: string | 0;
  "data-testid"?: string;
  "data-selected"?: boolean;
  className?: string;
}

type ButtonVariant =
  | "secondary"
  | "ghost"
  | "marketing"
  | "success"
  | "destructive-primary"
  | "destructive-ghost"
  | "outline";

export interface ButtonProps extends BaseButtonProps {
  children: ReactNode;
  variant?: ButtonVariant | false;
  size?: Extract<Size, "xs" | "sm"> | false;
  leftIconName?: IconName;
  rightIconName?: IconName;
  iconColor?: string;
  to?:
    | string
    | {
        pathname: string;
        search?: string;
        hash?: string;
        state?: any;
      };
  isExternalLink?: boolean;
  openInNewTab?: boolean;
  // This sets a min-width in case the button is intended to extend past its contents. A minimum padding will be maintained
  width?: string;
  // This is used to essentially reduce side padding in rare cases where the button width is constrained by a parent element
  withNoWrap?: boolean;
  withDropShadow?: boolean;
}

/**
 * Figma: https://cutt.ly/uilibrary-button
 */
const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant: _variant,
      leftIconName,
      rightIconName,
      iconColor,
      to,
      isExternalLink = false,
      openInNewTab = false,
      withDropShadow = false,
      withNoWrap = false,
      size: _size,
      width,
      margin = 0,
      children,
      id,
      name,
      type: _type,
      disabled,
      form,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      "data-testid": dataTestId,
      "data-selected": dataSelected,
      className,
    },
    ref
  ) => {
    // variant and size can be false to allow for using && to dynamically set non-default values
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const variant = _variant || "primary";
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const size = _size || "md";
    const type = _type ?? "button";

    let contentColor = COLORS.WHITE;
    switch (variant) {
      case "secondary":
      case "ghost": {
        contentColor = COLORS.HALO_BLUE;
        break;
      }
      case "success": {
        contentColor = COLORS.GREEN_800;
        break;
      }
      case "destructive-ghost": {
        contentColor = COLORS.RED_700;
        break;
      }
      case "outline": {
        contentColor = COLORS.BLACK;
        break;
      }
    }

    let as: ReactNode;
    if (to) {
      as = isExternalLink ? "a" : Link;
    }

    return (
      <StyledButton
        ref={ref}
        to={!isExternalLink ? to : undefined}
        href={isExternalLink ? to : undefined}
        as={as}
        target={openInNewTab ? "_blank" : undefined}
        rel={isExternalLink && openInNewTab ? "noopener noreferrer" : undefined}
        width={width}
        margin={margin}
        id={id}
        name={name}
        // Seems like button type has issues with default values and ESLint
        // https://github.com/yannickcr/eslint-plugin-react/issues/1555#issuecomment-377339656
        /* eslint-disable react/button-has-type */
        type={type}
        disabled={disabled}
        form={form}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid={dataTestId}
        data-selected={dataSelected}
        className={className}
        variant={variant}
        size={size}
        contentColor={contentColor}
        leftIconName={leftIconName}
        rightIconName={rightIconName}
        withDropShadow={withDropShadow}
        withNoWrap={withNoWrap}
      >
        {leftIconName && <Icon name={leftIconName} color={iconColor ?? contentColor} size={size} />}
        {children}
        {rightIconName && (
          <Icon name={rightIconName} color={iconColor ?? contentColor} size={size} />
        )}
      </StyledButton>
    );
  }
);

Button.displayName = "Button";

export default Button;

const StyledButton = styled.button`
  all: unset;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ size }) => (size === "xs" ? SPACINGS.SIZE_PX : SPACINGS.SIZE_0_5)};
  box-sizing: border-box;
  width: ${({ width }) => width ?? "initial"};
  min-width: ${({ width }) => width ?? "fit-content"};
  margin: ${({ margin }) => margin};
  color: ${({ contentColor }) => contentColor};
  border-radius: ${SPACINGS.SIZE_1};
  ${({ withDropShadow }) => withDropShadow && `box-shadow: ${STYLES.SHADOW_BUTTON}`};
  ${({ withNoWrap }) => withNoWrap && `white-space: nowrap;`};
  cursor: pointer;
  transition: background-color 0.1s ease;

  ${({ size, leftIconName, rightIconName }) => {
    let leftPadding = size === "xs" ? SPACINGS.SIZE_2 : SPACINGS.SIZE_4;
    if (rightIconName && !leftIconName) {
      leftPadding = size === "xs" ? SPACINGS.SIZE_3 : SPACINGS.SIZE_5;
    }
    let rightPadding = size === "xs" ? SPACINGS.SIZE_2 : SPACINGS.SIZE_4;
    if (!rightIconName && leftIconName) {
      rightPadding = size === "xs" ? SPACINGS.SIZE_3 : SPACINGS.SIZE_5;
    }

    switch (size) {
      case "xs": {
        return `
          height: 28px;
          padding: 0 ${rightPadding} 0 ${leftPadding};
          ${FONTS.BUTTON_3}
        `;
      }
      case "sm": {
        return `
          height: 36px;
          padding: 0 ${rightPadding} 0 ${leftPadding};
          ${FONTS.BUTTON_2}
        `;
      }
      case "md": {
        return `
          height: 42px;
          padding: 0 ${rightPadding} 0 ${leftPadding};
          ${FONTS.BUTTON_1}
        `;
      }
    }
  }};

  ${({ variant }) => {
    const borderSize = "1px solid";

    switch (variant) {
      case "primary": {
        return `
          background-color: ${COLORS.HALO_BLUE};
          border: ${borderSize} ${COLORS.BLUE_650};
          &:hover {
            background-color: ${COLORS.BLUE_600};
          }
          &:active {
            background-color: ${COLORS.BLUE_600};
          }
        `;
      }
      case "secondary": {
        return `
          background-color: ${COLORS.BLUE_LIGHT_300};
          border: ${borderSize} ${COLORS.BLUE_LIGHT_500};
          &:hover {
            background-color: ${COLORS.BLUE_LIGHT_400};
          }
          &:active {
            background-color: ${COLORS.BLUE_LIGHT_500};
          }
        `;
      }
      case "ghost": {
        return `
          background-color: ${COLORS.WHITE};
          &:hover {
            background-color: ${COLORS.BLUE_LIGHT_400};
          }
          &:active {
            background-color: ${COLORS.BLUE_LIGHT_500};
          }
        `;
      }
      case "marketing": {
        return `
          background: ${COLORS.BLUE_GRADIENT};
          box-shadow: ${STYLES.SHADOW_BUTTON};
          &:hover {
            background: ${COLORS.BLUE_GRADIENT_DARK};
          }
          &:active {
            background: ${COLORS.BLUE_GRADIENT_DARK};
          }
        `;
      }
      case "success": {
        return `
          background-color: ${COLORS.GREEN_100};
          border: ${borderSize} ${COLORS.GREEN_300};
          &:hover {
            background-color: ${COLORS.GREEN_200};
          }
          &:active {
            background-color: ${COLORS.GREEN_300};
            border-color: ${COLORS.GREEN_400};
          }
        `;
      }
      case "destructive-primary": {
        return `
          background-color: ${COLORS.RED_500};
          border: ${borderSize} ${COLORS.RED_600};
          &:hover {
            background-color: ${COLORS.RED_600};
            border-color: ${COLORS.RED_700};
          }
          &:active {
            background-color: ${COLORS.RED_700};
          }
        `;
      }
      case "destructive-ghost": {
        return `
          background-color: ${COLORS.WHITE};
          &:hover {
            background-color: ${COLORS.RED_100};
          }
          &:active {
            background-color: ${COLORS.RED_200};
          }
        `;
      }
      case "outline": {
        return `
          background-color: ${COLORS.WHITE};
          border: ${borderSize} ${COLORS.NEUTRAL_250};
          &:hover {
            background-color: ${COLORS.NEUTRAL_100};
          }
          &:active {
            background-color: ${COLORS.NEUTRAL_200};
          }
        `;
      }
    }
  }};

  :hover,
  :active,
  :focus,
  :visited {
    color: ${({ contentColor }) => contentColor};
    text-decoration: none;
  }

  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
`;
